import { useCallback, useEffect, useMemo, useState } from 'react';

export const useAdminGamification = () => {
  const [levels, setLevels] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [status, setStatus] = useState(null);
  const [salesSummary, setSalesSummary] = useState(null);
  const [lastSalesSyncResult, setLastSalesSyncResult] = useState(null);
  const [salesData, setSalesData] = useState({ rows: [], totals: null, loaded: false });
  const [salesSyncing, setSalesSyncing] = useState(false);
  const [salesDataLoading, setSalesDataLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [levelsRes, kpisRes, statusRes] = await Promise.all([
        fetch('/admin/api/gamification/levels'),
        fetch('/admin/api/gamification/kpis'),
        fetch('/admin/api/gamification/status')
      ]);
      const [levelsData, kpisData, statusData] = await Promise.all([
        levelsRes.json(),
        kpisRes.json(),
        statusRes.json()
      ]);
      if (!levelsRes.ok || levelsData?.error) throw new Error(levelsData?.error || 'No se cargaron niveles');
      if (!kpisRes.ok || kpisData?.error) throw new Error(kpisData?.error || 'No se cargaron KPIs');
      setLevels(levelsData.levels || []);
      setKpis(kpisData.kpis || []);
      setStatus(statusData.latest_sync || null);
      setSalesSummary(statusData.sales_summary || null);
    } catch (err) {
      setError(err?.message || 'No se pudo cargar gamificación.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateLevel = useCallback((id, field, value) => {
    setLevels((prev) => prev.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  }, []);

  const updateKpi = useCallback((id, field, value) => {
    setKpis((prev) => prev.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  }, []);

  const saveLevels = useCallback(async () => {
    setSaving(true);
    try {
      const response = await fetch('/admin/api/gamification/levels', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ levels })
      });
      const data = await response.json();
      if (!response.ok || data?.error) throw new Error(data?.error || 'No se pudieron guardar niveles');
      setLevels(data.levels || []);
    } finally {
      setSaving(false);
    }
  }, [levels]);

  const saveKpis = useCallback(async () => {
    setSaving(true);
    try {
      const response = await fetch('/admin/api/gamification/kpis', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kpis })
      });
      const data = await response.json();
      if (!response.ok || data?.error) throw new Error(data?.error || 'No se pudieron guardar KPIs');
      setKpis(data.kpis || []);
    } finally {
      setSaving(false);
    }
  }, [kpis]);

  const uploadLevelImage = useCallback(async (levelId, file) => {
    if (!levelId || !file) return;
    const formData = new FormData();
    formData.append('image', file);
    formData.append('entity_type', 'gamification_level');
    formData.append('entity_id', String(levelId));
    const response = await fetch('/admin/api/upload-image', {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    if (!response.ok || data?.error) throw new Error(data?.error || 'No se pudo subir la imagen');
    updateLevel(levelId, 'image_url', data.image_url);
  }, [updateLevel]);

  const syncSales = useCallback(async ({ startDate, endDate, daysBack = 90 }) => {
    setSalesSyncing(true);
    try {
      const response = await fetch('/admin/api/gamification/sales-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          days_back: daysBack
        })
      });
      const data = await response.json();
      if (!response.ok || data?.error) throw new Error(data?.error || 'No se pudieron sincronizar ventas');
      setSalesSummary(data.sales_summary || null);
      setLastSalesSyncResult(data.result || null);
      return data.result || {};
    } finally {
      setSalesSyncing(false);
    }
  }, []);

  const loadSalesData = useCallback(async ({ startDate, endDate, limit = 100 }) => {
    setSalesDataLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await fetch(`/admin/api/gamification/sales-data?${params.toString()}`);
      const data = await response.json();
      if (!response.ok || data?.error) throw new Error(data?.error || 'No se pudieron consultar ventas');
      setSalesData({
        rows: data.rows || [],
        totals: data.totals || null,
        loaded: true
      });
      return data;
    } finally {
      setSalesDataLoading(false);
    }
  }, []);

  return useMemo(() => ({
    levels,
    kpis,
    status,
    salesSummary,
    lastSalesSyncResult,
    salesData,
    loading,
    saving,
    salesSyncing,
    salesDataLoading,
    error,
    updateLevel,
    updateKpi,
    saveLevels,
    saveKpis,
    uploadLevelImage,
    syncSales,
    loadSalesData,
    reload: load
  }), [levels, kpis, status, salesSummary, lastSalesSyncResult, salesData, loading, saving, salesSyncing, salesDataLoading, error, updateLevel, updateKpi, saveLevels, saveKpis, uploadLevelImage, syncSales, loadSalesData, load]);
};
